import BasicModal from '__components/Modals/presets/BasicModal';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { GenericOption, SmartConnector } from 'pages/SmartConnectors/types';
import {
  SmartConnectorContext,
  SmartConnectorPatchResult,
} from 'pages/SmartConnectors/SmartConnector/SmartConnectorWizard/context';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import useModal from '__components/Modals/useModal';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import Button from '__components/Button';
import { StyledLink, StyledNotice, StyledScriptExample } from './styles';
import { KizenTypography } from '__app/typography';
import { SecretsMultiSelect } from './SecretsMultiselect';
import { ParameterItem } from './SQLParameter';
import { SQLParameterList } from './SQLParameterList';
import {
  CODE_BLOCK_LANGUAGES,
  toCodeBlock,
} from 'components/WYSIWYG/extensions/code-block';

export type SecretsAndParametersErrors = {
  integration_secrets?: string[];
  sql_parameters?: string[];
};

const DocLink: FC<PropsWithChildren<{ href: string }>> = ({
  href,
  children,
}) => (
  <StyledLink
    type="anchor"
    target="_blank"
    rel="noopener noreferrer"
    href={href}
  >
    {children}
  </StyledLink>
);

const ScriptExample: FC<{ script: string }> = ({ script }) => (
  <StyledScriptExample
    enableToolbar={false}
    enableResize={false}
    initialValue={toCodeBlock(script, CODE_BLOCK_LANGUAGES.sql.lang)}
    initialHeight={24}
    disabled
    codeBlockLanguage={CODE_BLOCK_LANGUAGES.sql.key}
    enableTextStyles={false}
  />
);

type SecretsAndParametersModalProps = {
  show: boolean;
  onConfirm: (
    data: Pick<SmartConnector, 'integration_secrets' | 'sql_parameters'>
  ) => Promise<{ success: boolean }>;
  onHide: () => void;
  errors: SecretsAndParametersErrors;
  setErrors: Dispatch<SetStateAction<SecretsAndParametersErrors>>;
};

export const SecretsAndParametersModal: FC<SecretsAndParametersModalProps> = ({
  show,
  onConfirm,
  onHide,
  errors,
  setErrors,
}) => {
  const { t } = useTranslation();

  const { smartConnector, showDeactivationModalWithResult } = useContext(
    SmartConnectorContext
  );
  const [integrationSecrets, setIntegrationSecrets] = useState(
    smartConnector.integration_secrets || []
  );
  const [sqlParameters, setSqlParameters] = useState<ParameterItem[]>(() =>
    Object.entries(smartConnector.sql_parameters || {}).map(([key, value]) => ({
      id: uuidv4(),
      name: key,
      value,
    }))
  );
  const [submitting, setSubmitting] = useState(false);

  const isDirty = useMemo(() => {
    return (
      integrationSecrets.length !==
        smartConnector.integration_secrets?.length ||
      integrationSecrets.some((secret, index) => {
        const smartConnectorSecret =
          smartConnector.integration_secrets?.[index];
        return !smartConnectorSecret || secret !== smartConnectorSecret;
      }) ||
      sqlParameters.length !==
        Object.keys(smartConnector.sql_parameters || {}).length ||
      sqlParameters.some((param) => {
        const smartConnectorParam = smartConnector.sql_parameters?.[param.name];
        return (
          smartConnectorParam === undefined ||
          param.value !== smartConnectorParam
        );
      })
    );
  }, [
    smartConnector.integration_secrets,
    integrationSecrets,
    sqlParameters,
    smartConnector.sql_parameters,
  ]);

  const onChangeIntegrationSecrets = useCallback(
    (value: GenericOption[]) => {
      setIntegrationSecrets(value.map((item) => item.value));
      setErrors((prevErrors) => ({
        ...prevErrors,
        integration_secrets: undefined,
      }));
    },
    [setErrors]
  );

  const handleSubmit = useCallback(async () => {
    const handleSave = async (): Promise<SmartConnectorPatchResult> => {
      const result = await onConfirm({
        integration_secrets: integrationSecrets,
        sql_parameters: sqlParameters.reduce(
          (acc, { name, value }) => (name ? { ...acc, [name]: value } : acc),
          {}
        ),
      });
      setSubmitting(false);
      if (result.success) {
        onHide();
      }
      return [result.success, null];
    };

    setSubmitting(true);

    if (getIsSmartConnectorActive(smartConnector)) {
      const [updated] = await showDeactivationModalWithResult({
        handleSave,
        handleDiscard: () => {
          onHide();
          return [null, null];
        },
      });

      if (!updated) {
        setSubmitting(false);
        throw new Error('Deactivation failed');
      }
    } else {
      await handleSave();
    }
  }, [
    integrationSecrets,
    onConfirm,
    showDeactivationModalWithResult,
    smartConnector,
    onHide,
    sqlParameters,
  ]);

  const [confirmationModalProps, , { show: showConfirmationModal }] = useModal({
    handleSubmit: onHide,
  });

  const onHideConfirmed = useCallback(() => {
    if (!isDirty) {
      onHide();
    } else {
      showConfirmationModal();
    }
  }, [onHide, isDirty, showConfirmationModal]);

  return (
    <>
      <BasicModal
        size="large"
        show={show}
        onConfirm={handleSubmit}
        onHide={onHideConfirmed}
        heading={t('Secrets and SQL Parameters')}
        disabled={!isDirty || submitting || sqlParameters.some((p) => p.error)}
        fitContent={false}
      >
        <StyledNotice>
          <Trans
            i18nKey="Each selected Integration Secret will be exposed to SQL as a <namedCollectionLink>NAMED COLLECTION</namedCollectionLink>, using the api_name (seen in parenthesis for selected Integration Secrets). The Value of the integration secret must be JSON, and it's key/value pairs will be reflected in the NAMED COLLECTION. The key/value pairs must match the expected arguments of a <tableFunctionsLink>clickhouse table function</tableFunctionsLink>.<br/><br/><bold>Note</bold>: Queries and requests to external systems will be performed by Test Executions and Dry Runs as well as Live executions. Keep this in mind if your Smart Connector SQL performs writes or large reads.<br/><br/>Parameters will be exposed to SQL in the table meta.parameters(name String, value String).<br/>Example: <script/>"
            components={{
              bold: <KizenTypography weight="bold" as="span" />,
              namedCollectionLink: (
                <DocLink href="https://clickhouse.com/docs/operations/named-collections" />
              ),
              tableFunctionsLink: (
                <DocLink href="https://clickhouse.com/docs/sql-reference/table-functions" />
              ),
              script: (
                <ScriptExample script="SELECT value FROM meta.parameters WHERE name = 'my_parameter';" />
              ),
            }}
          />
        </StyledNotice>
        <SecretsMultiSelect
          value={integrationSecrets}
          onChange={onChangeIntegrationSecrets}
          error={errors.integration_secrets}
        />
        <SQLParameterList
          sqlParameters={sqlParameters}
          setSqlParameters={setSqlParameters}
        />
      </BasicModal>
      <ConfirmationModal
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        defaultLeftBtnText={t('Save Changes')}
        actionBtnColor="red"
        leftBtn={
          <Button disabled={submitting || sqlParameters.some((p) => p.error)} />
        }
        defaultLeftBtnHandler={handleSubmit}
        {...confirmationModalProps}
      >
        {t(
          'You have unsaved changes. Would you like to save them or discard them before continuing?'
        )}
      </ConfirmationModal>
    </>
  );
};
